import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { reviewService } from '@/services/reviewService';
import { toReviewError, type ReviewError } from '../utils/errorMap';
import { filterKey } from '../utils/filterQuery';
import type { PaginatedSubmissions, SubmissionListParams } from '../types/submission';

interface UseSubmissionsReturn {
  page: PaginatedSubmissions | null;
  loading: boolean;
  error: ReviewError | null;
  refetch: () => void;
}

/**
 * Paginated submissions list.
 *
 * Idle while `resource` is undefined. Refetches whenever the resource
 * name or any filter value changes (compared by a stable string key
 * so the caller can construct `params` fresh each render without
 * looping).
 *
 * Uses the mounted-flag cleanup pattern for Strict Mode safety —
 * see `useReviewableResources` for the reasoning.
 */
export function useSubmissions(
  resource: string | undefined,
  params: SubmissionListParams | undefined,
): UseSubmissionsReturn {
  const [page, setPage] = useState<PaginatedSubmissions | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(resource));
  const [error, setError] = useState<ReviewError | null>(null);
  const [nonce, setNonce] = useState(0);

  // Value-based dep key so a freshly-built object with unchanged
  // values doesn't trigger a refetch.
  const key = useMemo(() => filterKey(params), [params]);

  // Latest params kept in a ref so the effect can read them without
  // being re-triggered by identity changes.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  useEffect(() => {
    if (!resource) {
      setPage(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    reviewService
      .listSubmissions(resource, paramsRef.current)
      .then((data) => {
        if (cancelled) return;
        // Diagnostic: shows the normalised page envelope so pagination
        // hint mismatches (missing `total` / `has_next_page`, aliased
        // field names, nested envelopes) are visible from the browser
        // console. Kept until the wire shape is verified against every
        // resource on staging.
        // eslint-disable-next-line no-console
        console.info(
          '[review] submissions →',
          { resource, params: paramsRef.current },
          {
            rows: data.data.length,
            total: data.total,
            page: data.page,
            page_size: data.page_size,
            has_next_page: data.has_next_page,
          },
        );
        setPage(data);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('[review] submissions failed', err);
        setError(toReviewError(err));
        setPage(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, key, nonce]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return { page, loading, error, refetch };
}
